import React from 'react';
import { Card } from "react-bootstrap";
import styles from '../SessionHistoryDetailView.module.css';
import { formatDateOnly } from '../../utils/formatDateOnly';

const AppointmentDetailsSection = ({
  clientName,
  providerName,
  appointmentDate,
  appointmentTime,
  durationDisplay,
  sessionFormData,
  updateFormData,
  timeInParts,
  timeOutParts,
  isTimeInPickerOpen,
  isTimeOutPickerOpen,
  timeInPickerRef,
  timeOutPickerRef,
  handleTimeInputFocus,
  setIsTimeInPickerOpen,
  setIsTimeOutPickerOpen,
  handleTimeSegmentChange,
  setTimeInParts,
  setTimeOutParts,
  handleTimeSegmentBlur,
  handleHourAdjust,
  handleMinuteAdjust,
  handlePeriodToggle,
  formatDate,
  isFollowUp = false,
  // Diagnosis props (only used when isFollowUp is true)
  primaryDiagnosisError,
  diagnosisCodeRef,
  secondaryDiagnosisCodeRef,
  tertiaryDiagnosisCodeRef,
  isPrimaryDiagnosisDropdownOpen,
  isSecondaryDiagnosisDropdownOpen,
  isTertiaryDiagnosisDropdownOpen,
  togglePrimaryDiagnosisDropdown,
  toggleSecondaryDiagnosisDropdown,
  toggleTertiaryDiagnosisDropdown,
  primaryDiagnosisSearchTerm,
  secondaryDiagnosisSearchTerm,
  tertiaryDiagnosisSearchTerm,
  setPrimaryDiagnosisSearchTerm,
  setSecondaryDiagnosisSearchTerm,
  setTertiaryDiagnosisSearchTerm,
  diagnosisSearchLoading,
  secondaryDiagnosisSearchLoading,
  tertiaryDiagnosisSearchLoading,
  diagnosisSearchError,
  secondaryDiagnosisSearchError,
  tertiaryDiagnosisSearchError,
  primaryDiagnosisResults,
  secondaryDiagnosisResults,
  tertiaryDiagnosisResults,
  handlePrimaryDiagnosisSelect,
  handleSecondaryDiagnosisSelect,
  handleTertiaryDiagnosisSelect,
  setIsPrimaryDiagnosisDropdownOpen,
  setIsSecondaryDiagnosisDropdownOpen,
  setIsTertiaryDiagnosisDropdownOpen
}) => {
  return (
    <Card id="appointment-details" className={styles.card}>
      <h3 className={styles.sectionHeading}>
        Appointment Details
      </h3>
      <div className={styles.twoColumnLayout}>
        {/* Left Column */}
        <div className={styles.column}>
          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Client
            </label>
            <span className={styles.valueSpan}>
              {clientName}
            </span>
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Date
            </label>
            <span className={styles.valueSpan}>
              {appointmentDate ? formatDateOnly(appointmentDate) : "N/A"}
            </span>
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Time In
            </label>
            <div
              className={styles.timePickerWrapper}
              ref={timeInPickerRef}
              onClick={() => handleTimeInputFocus(setIsTimeInPickerOpen)}
            >
              <div className={styles.timePickerInputGroup}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  className={styles.timePickerSegment}
                  value={timeInParts.hour}
                  onChange={(e) => handleTimeSegmentChange("timeIn", setTimeInParts, "hour", e.target.value)}
                  onFocus={() => handleTimeInputFocus(setIsTimeInPickerOpen)}
                  onBlur={() => handleTimeSegmentBlur("timeIn", setTimeInParts, "hour")}
                  placeholder="hh"
                  autoComplete="off"
                />
                <span className={styles.timePickerInputSeparator}>:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  className={styles.timePickerSegment}
                  value={timeInParts.minute}
                  onChange={(e) => handleTimeSegmentChange("timeIn", setTimeInParts, "minute", e.target.value)}
                  onFocus={() => handleTimeInputFocus(setIsTimeInPickerOpen)}
                  onBlur={() => handleTimeSegmentBlur("timeIn", setTimeInParts, "minute")}
                  placeholder="mm"
                  autoComplete="off"
                />
                <input
                  type="text"
                  maxLength={2}
                  className={`${styles.timePickerSegment} ${styles.timePickerSegmentPeriod}`}
                  value={timeInParts.period ? timeInParts.period.toUpperCase() : ""}
                  onChange={(e) => handleTimeSegmentChange("timeIn", setTimeInParts, "period", e.target.value)}
                  onFocus={() => handleTimeInputFocus(setIsTimeInPickerOpen)}
                  placeholder="AM"
                  autoComplete="off"
                />
              </div>
              {isTimeInPickerOpen && (
                <div className={styles.timePickerDropdown}>
                  <div className={styles.timePickerColumn}>
                    <button
                      type="button"
                      className={styles.timePickerControlButton}
                      onClick={() => handleHourAdjust("timeIn", setTimeInParts, 1)}
                      aria-label="Increase hour"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.timePickerValue}>
                      {timeInParts.hour ? parseInt(timeInParts.hour, 10) : "--"}
                    </div>
                    <button
                      type="button"
                      className={`${styles.timePickerControlButton} ${styles.timePickerControlButtonDown}`}
                      onClick={() => handleHourAdjust("timeIn", setTimeInParts, -1)}
                      aria-label="Decrease hour"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className={styles.timePickerColon}>:</div>
                  <div className={styles.timePickerColumn}>
                    <button
                      type="button"
                      className={styles.timePickerControlButton}
                      onClick={() => handleMinuteAdjust("timeIn", setTimeInParts, 1)}
                      aria-label="Increase minutes"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.timePickerValue}>
                      {timeInParts.minute ? timeInParts.minute : "--"}
                    </div>
                    <button
                      type="button"
                      className={`${styles.timePickerControlButton} ${styles.timePickerControlButtonDown}`}
                      onClick={() => handleMinuteAdjust("timeIn", setTimeInParts, -1)}
                      aria-label="Decrease minutes"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className={styles.timePickerColumn}>
                    <button
                      type="button"
                      className={styles.timePickerControlButton}
                      onClick={() => handlePeriodToggle("timeIn", setTimeInParts)}
                      aria-label="Toggle period"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.timePickerValue}>
                      {timeInParts.period ? timeInParts.period.toUpperCase() : "AM"}
                    </div>
                    <button
                      type="button"
                      className={`${styles.timePickerControlButton} ${styles.timePickerControlButtonDown}`}
                      onClick={() => handlePeriodToggle("timeIn", setTimeInParts)}
                      aria-label="Toggle period"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Duration
            </label>
            <span className={styles.valueSpan}>
              {durationDisplay}
            </span>
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Provider
            </label>
            <span className={styles.valueSpan}>
              {providerName}
            </span>
          </div>
          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Scheduled Time
            </label>
            <span className={styles.valueSpan}>
              {appointmentTime}
            </span>
          </div>
          <div className={styles.fieldRow}>
            <label className={styles.label}>
              Time Out
            </label>
            <div
              className={styles.timePickerWrapper}
              ref={timeOutPickerRef}
              onClick={() => handleTimeInputFocus(setIsTimeOutPickerOpen)}
            >
              <div className={styles.timePickerInputGroup}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  className={styles.timePickerSegment}
                  value={timeOutParts.hour}
                  onChange={(e) => handleTimeSegmentChange("timeOut", setTimeOutParts, "hour", e.target.value)}
                  onFocus={() => handleTimeInputFocus(setIsTimeOutPickerOpen)}
                  onBlur={() => handleTimeSegmentBlur("timeOut", setTimeOutParts, "hour")}
                  placeholder="hh"
                  autoComplete="off"
                />
                <span className={styles.timePickerInputSeparator}>:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  className={styles.timePickerSegment}
                  value={timeOutParts.minute}
                  onChange={(e) => handleTimeSegmentChange("timeOut", setTimeOutParts, "minute", e.target.value)}
                  onFocus={() => handleTimeInputFocus(setIsTimeOutPickerOpen)}
                  onBlur={() => handleTimeSegmentBlur("timeOut", setTimeOutParts, "minute")}
                  placeholder="mm"
                  autoComplete="off"
                />
                <input
                  type="text"
                  maxLength={2}
                  className={`${styles.timePickerSegment} ${styles.timePickerSegmentPeriod}`}
                  value={timeOutParts.period ? timeOutParts.period.toUpperCase() : ""}
                  onChange={(e) => handleTimeSegmentChange("timeOut", setTimeOutParts, "period", e.target.value)}
                  onFocus={() => handleTimeInputFocus(setIsTimeOutPickerOpen)}
                  placeholder="AM"
                  autoComplete="off"
                />
              </div>
              {isTimeOutPickerOpen && (
                <div className={styles.timePickerDropdown}>
                  <div className={styles.timePickerColumn}>
                    <button
                      type="button"
                      className={styles.timePickerControlButton}
                      onClick={() => handleHourAdjust("timeOut", setTimeOutParts, 1)}
                      aria-label="Increase hour"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.timePickerValue}>
                      {timeOutParts.hour ? parseInt(timeOutParts.hour, 10) : "--"}
                    </div>
                    <button
                      type="button"
                      className={`${styles.timePickerControlButton} ${styles.timePickerControlButtonDown}`}
                      onClick={() => handleHourAdjust("timeOut", setTimeOutParts, -1)}
                      aria-label="Decrease hour"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className={styles.timePickerColon}>:</div>
                  <div className={styles.timePickerColumn}>
                    <button
                      type="button"
                      className={styles.timePickerControlButton}
                      onClick={() => handleMinuteAdjust("timeOut", setTimeOutParts, 1)}
                      aria-label="Increase minutes"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.timePickerValue}>
                      {timeOutParts.minute ? timeOutParts.minute : "--"}
                    </div>
                    <button
                      type="button"
                      className={`${styles.timePickerControlButton} ${styles.timePickerControlButtonDown}`}
                      onClick={() => handleMinuteAdjust("timeOut", setTimeOutParts, -1)}
                      aria-label="Decrease minutes"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className={styles.timePickerColumn}>
                    <button
                      type="button"
                      className={styles.timePickerControlButton}
                      onClick={() => handlePeriodToggle("timeOut", setTimeOutParts)}
                      aria-label="Toggle period"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.timePickerValue}>
                      {timeOutParts.period ? timeOutParts.period.toUpperCase() : "AM"}
                    </div>
                    <button
                      type="button"
                      className={`${styles.timePickerControlButton} ${styles.timePickerControlButtonDown}`}
                      onClick={() => handlePeriodToggle("timeOut", setTimeOutParts)}
                      aria-label="Toggle period"
                    >
                      <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true" focusable="false">
                        <polyline
                          points="6 15 12 9 18 15"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.separator}></div>

      <div className={styles.fieldRowVertical}>
        <label className={styles.labelMinWidth}>
          Was the client at home?
        </label>
        <select
          className={styles.select}
          value={sessionFormData.wasClientAtHome || ""}
          onChange={(e) => updateFormData("wasClientAtHome", e.target.value)}
        >
          <option value="">-</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {/* Diagnosis Section - only render when isFollowUp is true */}
      {isFollowUp && (
        <>
          <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
          
          <h3 className={styles.sectionHeading} style={{ marginBottom: "24px" }}>
            Diagnosis
          </h3>

          {/* Primary Diagnosis - Required */}
          <div style={{ marginBottom: "16px" }}>
            <label className={styles.label} style={{ display: "block", marginBottom: "8px", textAlign: "left", fontWeight: "500" }}>
              Primary Diagnosis
            </label>
            <div style={{ position: "relative", width: "100%" }} ref={diagnosisCodeRef}>
              <div
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "16px",
                  color: sessionFormData.primaryDiagnosis ? "#000" : "#8E8E93",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px"
                }}
                onClick={togglePrimaryDiagnosisDropdown}
              >
                <span style={{ flex: "1", textAlign: "left" }}>
                  {sessionFormData.primaryDiagnosis && sessionFormData.primaryDiagnosisDescription
                    ? `${sessionFormData.primaryDiagnosis} - ${sessionFormData.primaryDiagnosisDescription}`
                    : "-"
                  }
                </span>
                <span style={{ marginLeft: "8px" }}>▼</span>
              </div>
              
              {primaryDiagnosisError && (
                <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
                  {primaryDiagnosisError}
                </div>
              )}

              {isPrimaryDiagnosisDropdownOpen && (
                <div className={styles.diagnosisDropdownPanel} style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, marginTop: "4px" }}>
                  <div className={styles.diagnosisSearchInput}>
                    <svg
                      className={styles.diagnosisSearchIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 14L11.1 11.1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="text"
                      value={primaryDiagnosisSearchTerm}
                      onChange={(e) => setPrimaryDiagnosisSearchTerm(e.target.value)}
                      placeholder="Type here to search through 1000's of ICD-10 codes"
                      className={styles.diagnosisSearchField}
                    />
                  </div>
                  <div className={styles.diagnosisResults}>
                    {diagnosisSearchLoading ? (
                      <div className={styles.diagnosisStatusMessage}>Searching ICD-10 codes...</div>
                    ) : diagnosisSearchError ? (
                      <div className={styles.diagnosisStatusMessage}>{diagnosisSearchError}</div>
                    ) : primaryDiagnosisResults.length === 0 ? (
                      <div className={styles.diagnosisStatusMessage}>No diagnosis codes found.</div>
                    ) : (
                      primaryDiagnosisResults.map((result) => (
                        <button
                          type="button"
                          key={`${result.code}-${result.description}`}
                          className={styles.diagnosisResultRow}
                          onClick={() => {
                            handlePrimaryDiagnosisSelect(result);
                            setIsPrimaryDiagnosisDropdownOpen(false);
                          }}
                        >
                          <span>{result.code}</span> - {result.description}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Secondary Diagnosis - Optional */}
          <div style={{ marginBottom: "16px" }}>
            <label className={styles.label} style={{ display: "block", marginBottom: "8px", textAlign: "left", fontWeight: "500" }}>
              Secondary Diagnosis
            </label>
            <div style={{ position: "relative", width: "100%" }} ref={secondaryDiagnosisCodeRef}>
              <div
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "16px",
                  color: sessionFormData.secondaryDiagnosis ? "#000" : "#8E8E93",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px"
                }}
                onClick={toggleSecondaryDiagnosisDropdown}
              >
                <span style={{ flex: "1", textAlign: "left" }}>
                  {sessionFormData.secondaryDiagnosis && sessionFormData.secondaryDiagnosisDescription
                    ? `${sessionFormData.secondaryDiagnosis} - ${sessionFormData.secondaryDiagnosisDescription}`
                    : "-"
                  }
                </span>
                <span style={{ marginLeft: "8px" }}>▼</span>
              </div>

              {isSecondaryDiagnosisDropdownOpen && (
                <div className={styles.diagnosisDropdownPanel} style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, marginTop: "4px" }}>
                  <div className={styles.diagnosisSearchInput}>
                    <svg
                      className={styles.diagnosisSearchIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 14L11.1 11.1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="text"
                      value={secondaryDiagnosisSearchTerm}
                      onChange={(e) => setSecondaryDiagnosisSearchTerm(e.target.value)}
                      placeholder="Type here to search through 1000's of ICD-10 codes"
                      className={styles.diagnosisSearchField}
                    />
                  </div>
                  <div className={styles.diagnosisResults}>
                    {secondaryDiagnosisSearchLoading ? (
                      <div className={styles.diagnosisStatusMessage}>Searching ICD-10 codes...</div>
                    ) : secondaryDiagnosisSearchError ? (
                      <div className={styles.diagnosisStatusMessage}>{secondaryDiagnosisSearchError}</div>
                    ) : secondaryDiagnosisResults.length === 0 ? (
                      <div className={styles.diagnosisStatusMessage}>No diagnosis codes found.</div>
                    ) : (
                      secondaryDiagnosisResults.map((result) => (
                        <button
                          type="button"
                          key={`secondary-${result.code}-${result.description}`}
                          className={styles.diagnosisResultRow}
                          onClick={() => {
                            handleSecondaryDiagnosisSelect(result);
                            setIsSecondaryDiagnosisDropdownOpen(false);
                          }}
                        >
                          <span>{result.code}</span> - {result.description}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tertiary Diagnosis - Optional */}
          <div style={{ marginBottom: "16px" }}>
            <label className={styles.label} style={{ display: "block", marginBottom: "8px", textAlign: "left", fontWeight: "500" }}>
              Tertiary Diagnosis
            </label>
            <div style={{ position: "relative", width: "100%" }} ref={tertiaryDiagnosisCodeRef}>
              <div
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "16px",
                  color: sessionFormData.tertiaryDiagnosis ? "#000" : "#8E8E93",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px"
                }}
                onClick={toggleTertiaryDiagnosisDropdown}
              >
                <span style={{ flex: "1", textAlign: "left" }}>
                  {sessionFormData.tertiaryDiagnosis && sessionFormData.tertiaryDiagnosisDescription
                    ? `${sessionFormData.tertiaryDiagnosis} - ${sessionFormData.tertiaryDiagnosisDescription}`
                    : "-"
                  }
                </span>
                <span style={{ marginLeft: "8px" }}>▼</span>
              </div>

              {isTertiaryDiagnosisDropdownOpen && (
                <div className={styles.diagnosisDropdownPanel} style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, marginTop: "4px" }}>
                  <div className={styles.diagnosisSearchInput}>
                    <svg
                      className={styles.diagnosisSearchIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 14L11.1 11.1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="text"
                      value={tertiaryDiagnosisSearchTerm}
                      onChange={(e) => setTertiaryDiagnosisSearchTerm(e.target.value)}
                      placeholder="Type here to search through 1000's of ICD-10 codes"
                      className={styles.diagnosisSearchField}
                    />
                  </div>
                  <div className={styles.diagnosisResults}>
                    {tertiaryDiagnosisSearchLoading ? (
                      <div className={styles.diagnosisStatusMessage}>Searching ICD-10 codes...</div>
                    ) : tertiaryDiagnosisSearchError ? (
                      <div className={styles.diagnosisStatusMessage}>{tertiaryDiagnosisSearchError}</div>
                    ) : tertiaryDiagnosisResults.length === 0 ? (
                      <div className={styles.diagnosisStatusMessage}>No diagnosis codes found.</div>
                    ) : (
                      tertiaryDiagnosisResults.map((result) => (
                        <button
                          type="button"
                          key={`tertiary-${result.code}-${result.description}`}
                          className={styles.diagnosisResultRow}
                          onClick={() => {
                            handleTertiaryDiagnosisSelect(result);
                            setIsTertiaryDiagnosisDropdownOpen(false);
                          }}
                        >
                          <span>{result.code}</span> - {result.description}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Add Additional Diagnosis Link */}
          <div style={{ marginTop: "16px", textAlign: "left" }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                // This could trigger adding a 4th diagnosis or open a modal
                // For now, we'll just prevent default
              }}
              style={{
                color: "#20B2AA",
                textDecoration: "underline",
                fontSize: "16px",
                cursor: "pointer"
              }}
            >
              Add Additional Diagnosis
            </a>
          </div>
        </>
      )}
    </Card>
  );
};

export default AppointmentDetailsSection;

